import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { saveAs } from 'file-saver'
import { firstValueFrom } from 'rxjs'

import { appConstants } from '../../app.constants'
import { IncentiveMode } from '../enums/incentive-mode.enum'
import { TargetApiRoutes } from '../enums/target-api-routes.enum'
import { Environment } from '../interfaces/environment.interface'
import { Paginator } from '../interfaces/paginator.interface'
import { Field } from '../interfaces/resources/field.interface'
import { Incentive } from '../interfaces/resources/incentive.interface'
import { Theme } from '../interfaces/resources/theme.interface'
import { EnvironmentService } from './environment.service'
import { ThemeService } from './theme.service'

@Injectable({
  providedIn: 'root'
})
export class IncentiveService {
  environment: Environment

  constructor(
    private http: HttpClient,
    private themeService: ThemeService,
    private environmentService: EnvironmentService
  ) {
    this.environmentService.loadEnvironment().subscribe((env) => {
      this.environment = env
    })
  }

  list(listParams: {
    ids?: string[]
    withoutPagination?: boolean
    similarTo?: string
    fields?: { [key: string]: string }
    mode?: IncentiveMode
    sortBy?: string
    page?: number
  }): Promise<Paginator<Incentive> | Incentive[]> {
    let params: any = {}

    if (listParams.withoutPagination) {
      params.withoutPagination = true
    } else {
      params.page = listParams.page ? listParams.page.toString() : '1'
    }

    if (listParams.ids) {
      params.ids = listParams.ids.join(',')
    }

    if (listParams.fields) {
      Object.assign(params, listParams.fields)
    }

    if (listParams.similarTo) {
      params.similarTo = listParams.similarTo
    }

    if (listParams.sortBy) {
      params.sortBy = listParams.sortBy
    }

    if (listParams.mode) {
      params.mode = listParams.mode
    }

    if (params.withoutPagination) {
      return (
        firstValueFrom(
          this.http.get(
            `${this.environment.apiBaseUrl}${TargetApiRoutes.Incentives}`,
            {
              params: params
            }
          )
        ) as Promise<Incentive[]>
      ).then((incentiveRes: Incentive[]) =>
        incentiveRes.map((incentive) => this.formatIncentive(incentive))
      )
    } else {
      return (
        firstValueFrom(
          this.http.get(
            `${this.environment.apiBaseUrl}${TargetApiRoutes.Incentives}`,
            {
              params: params
            }
          )
        ) as Promise<Paginator<Incentive>>
      ).then((paginator: Paginator<Incentive>) => {
        paginator.data.map((incentive) => this.formatIncentive(incentive))
      }) as Promise<Paginator<Incentive>>
    }
  }

  listPaginated(
    listParams: {
      ids?: string[]
      withoutPagination?: boolean
      fields?: { [key: string]: string }
      mode?: IncentiveMode
      similarTo?: string
      page?: number
    } = {}
  ): Promise<Paginator<Incentive>> {
    return this.list({
      ...listParams,
      withoutPagination: false
    }) as Promise<Paginator<Incentive>>
  }

  listWithoutPagination(
    listParams: {
      ids?: string[]
      withoutPagination?: boolean
      fields?: { [key: string]: string }
      mode?: IncentiveMode
      similarTo?: string
      sortBy?: string
      page?: number
    } = {}
  ): Promise<Incentive[]> {
    return this.list({
      ...listParams,
      withoutPagination: true
    }) as Promise<Incentive[]>
  }

  show(id: string): Promise<Incentive> {
    return (
      firstValueFrom(
        this.http.get(
          `${this.environment.apiBaseUrl}${TargetApiRoutes.Incentive}`,
          {
            params: {
              id,
              mode: IncentiveMode.Detail
            }
          }
        )
      ) as Promise<Incentive>
    ).then((incentiveRes: Incentive) => {
      return this.formatIncentive(incentiveRes)
    })
  }

  async download(ids: string[]): Promise<void> {
    const incentives: Incentive[] = await this.listWithoutPagination({
      ids,
      mode: IncentiveMode.Export
    })

    const themes: Theme[] = await this.themeService.list({
      mode: IncentiveMode.Export
    })

    const flatFields: Field[] = themes.reduce(
      (acc: Field[], theme: Theme) => [
        ...acc,
        ...theme.fields,
        ...theme.subThemes.map((subTheme: Theme) => subTheme.fields).flat()
      ],
      []
    )

    let headings: string = flatFields
      .map((field) => `"${field.label}"`)
      .join(',')
    const content: string[] = incentives.map((incentive: Incentive) =>
      flatFields
        .map((flatField: Field) =>
          this.formatCSVString(incentive.values[flatField.id])
        )

        .join(',')
    )

    // Add Cyprus special note.
    if (incentives.every((incentive) => incentive.country.label === 'Cyprus')) {
      headings =
        `"${appConstants.cyprusNote.replace(/"/g, '""')}",` +
        flatFields.map((field) => `""`) +
        '\r\n' +
        headings
    }

    const csv = [headings, ...content].join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv' })

    saveAs(blob, 'INNOTAX incentive exports.csv')
  }

  formatIncentive(incentive: Incentive): Incentive {
    return {
      ...incentive,
      values: incentive.fields.reduce(
        (valueObject, field: Field) => ({
          ...valueObject,
          [field.id]: this.extractTooltip(field.value)
        }),
        {}
      ),
      tooltipValues: incentive.fields.reduce(
        (valueObject, field: Field) => ({
          ...valueObject,
          [field.id]: this.extractTooltip(field.value, true)
        }),
        {}
      ),
      isDisabled:
        incentive.fields.find((f) => f.id === 'f:active')?.value === 'Repealed'
    }
  }

  private formatCSVString(str: string): string {
    if (!str) {
      return '"-"'
    }
    return `"${str.replace(/"/g, '""')}"`
  }

  private extractTooltip(str: string, getTooltipPart?: boolean): string {
    const tooltipMarker: string = 'infoTooltip:'
    if (!str.includes(tooltipMarker)) {
      return getTooltipPart ? null : str
    }

    return getTooltipPart
      ? str.split(tooltipMarker)[1]
      : str.split(tooltipMarker)[0]
  }
}
